import { useMemo, useState, useEffect, useRef } from "preact/hooks"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { AutoSplitCandidate } from "./auto_split_wizard_types"
import { AutoSplitMappingPreview } from "./auto_split_mapping_preview"
import { AutoSplitMergePanel } from "./auto_split_merge_panel"
import { AutoSplitReview } from "./auto_split_review"
import { Input } from "@/components/ui/input"
import { OrgOption } from "@/api/org_options"

interface AutoSplitWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialCandidates: AutoSplitCandidate[]
  transactionAmount: number
  retailerOptions: OrgOption[]
  productLineOptions: OrgOption[]
  productCategoryOptions: OrgOption[]
  onFinalize: (candidates: AutoSplitCandidate[]) => void
}

const STEP_TITLES = ["Mapping Preview", "Merge Groups", "Final Review"]

export function AutoSplitWizard({
  open,
  onOpenChange,
  initialCandidates,
  transactionAmount,
  retailerOptions,
  productLineOptions,
  productCategoryOptions,
  onFinalize,
}: AutoSplitWizardProps) {
  const [stepIndex, setStepIndex] = useState(0)
  const [candidates, setCandidates] = useState<AutoSplitCandidate[]>(initialCandidates)
  const [searchQuery, setSearchQuery] = useState("")

  const autoMatchedStatsRef = useRef({ rows: 0, amountCents: 0 })
  const enforcedTotalStatsRef = useRef({ rows: 0, amountCents: 0 })

  useEffect(() => {
    if (open) {
      const initialized = initialCandidates.map((candidate: AutoSplitCandidate) => ({
        ...candidate,
        match_status: deriveCandidateStatus(
          candidate,
          retailerOptions,
          productLineOptions,
          productCategoryOptions
        ),
      }))
      setCandidates(initialized)
      const enforced = initialized.filter((c: AutoSplitCandidate) => hasEnforcedFields(c))
      enforcedTotalStatsRef.current = enforced.reduce(
        (acc, c: AutoSplitCandidate) => ({ rows: acc.rows + c.source_count, amountCents: acc.amountCents + c.source_amount_cents }),
        { rows: 0, amountCents: 0 }
      )
      const matched = enforced.filter((c: AutoSplitCandidate) => isFullyMatched(c))
      autoMatchedStatsRef.current = matched.reduce(
        (acc, c: AutoSplitCandidate) => ({ rows: acc.rows + c.source_count, amountCents: acc.amountCents + c.source_amount_cents }),
        { rows: 0, amountCents: 0 }
      )
      setStepIndex(0)
      setSearchQuery("")
    }
  }, [open, initialCandidates, retailerOptions, productLineOptions, productCategoryOptions])

  const warnings = useMemo(
    () => validateCandidates(candidates, transactionAmount),
    [candidates, transactionAmount]
  )

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setStepIndex(0)
      setSearchQuery("")
      setCandidates(
        initialCandidates.map((candidate: AutoSplitCandidate) => ({
          ...candidate,
          match_status: deriveCandidateStatus(
            candidate,
            retailerOptions,
            productLineOptions,
            productCategoryOptions
          ),
        }))
      )
    }
  }

  function updateCandidate(candidateId: string, patch: Partial<AutoSplitCandidate>) {
    setCandidates((prev) =>
      prev.map((candidate: AutoSplitCandidate) => {
        if (candidate.id !== candidateId) return candidate
        const patched = { ...candidate, ...patch }
        return {
          ...patched,
          match_status: deriveCandidateStatus(
            patched,
            retailerOptions,
            productLineOptions,
            productCategoryOptions
          ),
        }
      })
    )
  }

  function mergeCandidates(candidateIds: string[]) {
    if (candidateIds.length < 2) return
    const selected = candidates.filter((candidate: AutoSplitCandidate) => candidateIds.includes(candidate.id))
    const remaining = candidates.filter((candidate: AutoSplitCandidate) => !candidateIds.includes(candidate.id))
    const first = selected[0]
    if (!first) return

    const merged: AutoSplitCandidate = {
      ...first,
      id: `merged-${Date.now()}-${Math.random()}`,
      source_count: selected.reduce((sum, candidate) => sum + candidate.source_count, 0),
      source_amount: selected.reduce((sum, candidate) => sum + candidate.source_amount, 0),
      source_amount_cents: selected.reduce((sum, candidate) => sum + candidate.source_amount_cents, 0),
      raw_retailer: mergeDistinct(selected.map((c) => c.raw_retailer)),
      raw_product_description: mergeDistinct(selected.map((c) => c.raw_product_description)),
      raw_upc_key: mergeDistinct(selected.map((c) => c.raw_upc_key)),
    }
    merged.match_status = deriveCandidateStatus(
      merged,
      retailerOptions,
      productLineOptions,
      productCategoryOptions
    )

    setCandidates([...remaining, merged])
  }

  function goNext() {
    if (stepIndex < STEP_TITLES.length - 1) {
      setStepIndex(stepIndex + 1)
      return
    }

    onFinalize(candidates)
    handleOpenChange(false)
  }

  function goBack() {
    if (stepIndex === 0) return
    setStepIndex(stepIndex - 1)
  }

  const sortOrderRef = useRef<Set<string>>(new Set())
  const currentIds = new Set(candidates.map((c: AutoSplitCandidate) => c.id))
  const idsChanged = currentIds.size !== sortOrderRef.current.size || [...currentIds].some((id: string) => !sortOrderRef.current.has(id))
  const sortedOrderRef = useRef<string[]>([])
  if (idsChanged) {
    sortOrderRef.current = currentIds
    sortedOrderRef.current = [...candidates].sort(compareCandidatesForGuidance).map((c: AutoSplitCandidate) => c.id)
  }
  const sortedCandidates = useMemo(() => {
    const order = sortedOrderRef.current
    return [...candidates].sort((a: AutoSplitCandidate, b: AutoSplitCandidate) => order.indexOf(a.id) - order.indexOf(b.id))
  }, [candidates])
  const filteredCandidates = useMemo(
    () => sortedCandidates.filter((candidate: AutoSplitCandidate) => candidateMatchesSearch(candidate, searchQuery)),
    [sortedCandidates, searchQuery]
  )

  function handleInteractOutside(e: Event) {
    const target = e.target as HTMLElement
    if (
      target?.closest?.("[data-radix-popper-content-wrapper]") ||
      target?.closest?.("[role='listbox']") ||
      target?.closest?.("[cmdk-list]") ||
      target?.closest?.("[cmdk-input]") ||
      target?.closest?.("[cmdk-item]")
    ) {
      e.preventDefault()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] w-[calc(100vw*2/3)] max-w-[calc(100vw*2/3)] flex-col gap-4 overflow-hidden p-6"
        onInteractOutside={handleInteractOutside}
      >
        <DialogHeader className="shrink-0 space-y-1.5">
          <DialogTitle>Guided Auto Split</DialogTitle>
          <DialogDescription>
            Step {stepIndex + 1} of {STEP_TITLES.length}: {STEP_TITLES[stepIndex]}
          </DialogDescription>
        </DialogHeader>

        <div class="flex shrink-0 flex-col gap-3">
          <div class="flex items-center gap-3">
            <Input
              class="h-10 min-w-0 flex-1"
              placeholder="Search by retailer, product, UPC/item key, product line, or product group"
              value={searchQuery}
              onInput={(e: Event) => setSearchQuery((e.currentTarget as HTMLInputElement).value)}
            />
            <div class="text-sm text-gray-500 whitespace-nowrap">
              Showing {filteredCandidates.length} of {candidates.length}
            </div>
          </div>
        </div>

        <div class="max-h-[calc(92vh-16rem)] min-h-0 overflow-y-auto overscroll-contain pr-1">
          {stepIndex === 0 && (
            <AutoSplitMappingPreview
              candidates={filteredCandidates}
              allCandidates={candidates}
              onUpdateCandidate={updateCandidate}
              retailerOptions={retailerOptions}
              productLineOptions={productLineOptions}
              productCategoryOptions={productCategoryOptions}
              autoMatchedStats={autoMatchedStatsRef.current}
              enforcedTotalStats={enforcedTotalStatsRef.current}
            />
          )}
          {stepIndex === 1 && (
            <AutoSplitMergePanel candidates={filteredCandidates} onMergeCandidates={mergeCandidates} />
          )}
          {stepIndex === 2 && (
            <AutoSplitReview candidates={filteredCandidates} transactionAmount={transactionAmount} warnings={warnings} />
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={goBack} disabled={stepIndex === 0}>
            Back
          </Button>
          <Button onClick={goNext}>
            {stepIndex < STEP_TITLES.length - 1 ? "Next" : "Finalize Splits"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function mergeDistinct(values: string[]): string {
  const uniq = Array.from(new Set(values.map((value) => value.trim()).filter((value) => value.length > 0)))
  if (uniq.length === 0) return ""
  if (uniq.length === 1) return uniq[0]
  return "Multiple"
}

function validateCandidates(candidates: AutoSplitCandidate[], transactionAmount: number): string[] {
  const warnings: string[] = []
  const totalCents = candidates.reduce((sum, candidate) => sum + candidate.source_amount_cents, 0)
  const expectedCents = Math.round(Math.abs(transactionAmount) * 100)
  const total = totalCents / 100
  const expected = expectedCents / 100

  if (totalCents !== expectedCents) {
    warnings.push(`Split total (${total.toFixed(2)}) does not match expected amount (${expected.toFixed(2)}).`)
  }

  const inconsistentAmounts = candidates.filter((candidate: AutoSplitCandidate) => {
    return Math.round(candidate.source_amount * 100) !== candidate.source_amount_cents
  })
  if (inconsistentAmounts.length > 0) warnings.push(`${inconsistentAmounts.length} split(s) have inconsistent cents totals.`)

  const invalidDates = candidates.filter(
    (candidate: AutoSplitCandidate) =>
      candidate.execution_date && candidate.execution_date !== "Unknown" && !/^\d{4}-\d{2}-\d{2}$/.test(candidate.execution_date)
  )
  if (invalidDates.length > 0) {
    warnings.push(`${invalidDates.length} split(s) have non-ISO execution dates.`)
  }

  const unmatchedRetailer = candidates.filter(
    (candidate: AutoSplitCandidate) =>
      candidate.option_enforced.retailer && candidate.match_status.retailer === "unmatched"
  )
  if (unmatchedRetailer.length > 0) warnings.push(`${unmatchedRetailer.length} split(s) have unmatched retailer mappings.`)

  const unmatchedProductLine = candidates.filter(
    (candidate: AutoSplitCandidate) =>
      candidate.option_enforced.product_line && candidate.match_status.product_line === "unmatched"
  )
  if (unmatchedProductLine.length > 0) {
    warnings.push(`${unmatchedProductLine.length} split(s) have unmatched product line mappings.`)
  }

  const unmatchedProductCategory = candidates.filter(
    (candidate: AutoSplitCandidate) =>
      candidate.option_enforced.product_category && candidate.match_status.product_category === "unmatched"
  )
  if (unmatchedProductCategory.length > 0) {
    warnings.push(`${unmatchedProductCategory.length} split(s) have unmatched product group mappings.`)
  }

  return warnings
}

function deriveCandidateStatus(
  candidate: AutoSplitCandidate,
  retailerOptions: OrgOption[],
  productLineOptions: OrgOption[],
  productCategoryOptions: OrgOption[]
): AutoSplitCandidate["match_status"] {
  return {
    retailer: deriveFieldStatus(
      candidate.retailer_name,
      candidate.option_enforced.retailer,
      retailerOptions
    ),
    product_line: deriveFieldStatus(
      candidate.product_line,
      candidate.option_enforced.product_line,
      productLineOptions
    ),
    product_category: deriveFieldStatus(
      candidate.product_category,
      candidate.option_enforced.product_category,
      productCategoryOptions
    ),
  }
}

function deriveFieldStatus(value: string, optionEnforced: boolean, options: OrgOption[]): AutoSplitCandidate["match_status"]["retailer"] {
  const normalizedValue = value.trim().toLowerCase()
  if (!normalizedValue || normalizedValue === "unknown") return "unmatched"
  if (!optionEnforced) return "fallback"
  const inOptions = options.some((option) => option.option_value.trim().toLowerCase() === normalizedValue)
  return inOptions ? "matched" : "unmatched"
}

function compareCandidatesForGuidance(left: AutoSplitCandidate, right: AutoSplitCandidate): number {
  const leftPriority = candidateHasUnmatched(left) ? 0 : 1
  const rightPriority = candidateHasUnmatched(right) ? 0 : 1
  if (leftPriority !== rightPriority) return leftPriority - rightPriority
  return right.source_amount_cents - left.source_amount_cents
}

function candidateHasUnmatched(candidate: AutoSplitCandidate): boolean {
  return (
    candidate.match_status.retailer === "unmatched" ||
    candidate.match_status.product_line === "unmatched" ||
    candidate.match_status.product_category === "unmatched"
  )
}

function hasEnforcedFields(candidate: AutoSplitCandidate): boolean {
  return candidate.option_enforced.retailer || candidate.option_enforced.product_line || candidate.option_enforced.product_category
}

function isFullyMatched(candidate: AutoSplitCandidate): boolean {
  const enforced = candidate.option_enforced
  const status = candidate.match_status
  if (enforced.retailer && status.retailer === "unmatched") return false
  if (enforced.product_line && status.product_line === "unmatched") return false
  if (enforced.product_category && status.product_category === "unmatched") return false
  return true
}

function candidateMatchesSearch(candidate: AutoSplitCandidate, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  const searchableParts = [
    candidate.raw_retailer,
    candidate.raw_product_description,
    candidate.raw_upc_key,
    candidate.retailer_name,
    candidate.product_line,
    candidate.product_category,
    candidate.execution_date,
  ]
  return searchableParts.some((part) => part.toLowerCase().includes(normalizedQuery))
}
